import {
	Button,
	type ButtonProps,
	Text,
	TextLineOrSkeleton,
	useCopyToClipboard,
	useRouter
} from '@capsicum/common-web';
import { ChilizChainCircleIcon, CopyClipboardIcon } from '@capsicum/common-web/icons';
import styled, { useTheme } from 'styled-components';

import { useNCWAddressCopyCard } from '../../hooks/components/useNCWAddressCopyCard';

/**
 * This component displays the Chiliz Chain address and lets users copy it to the clipboard
 */
export const NCWAddressCopyCard = () => {
	const { spacings } = useTheme();
	const { addressNotAvailable, askUserToAwaitAddress, blockchainAddress, intlText, isLoading } =
		useNCWAddressCopyCard();
	const { push } = useRouter();

	const { copyToClipboard } = useCopyToClipboard({
		label: intlText.snackbarText,
		value: blockchainAddress ?? ''
	});

	const copyButtonProps = {
		buttonElementStyle: {
			marginInline: spacings.medium
		},
		color: 'brand',
		disabled: addressNotAvailable,
		iconEnd: { icon: { src: CopyClipboardIcon } },
		isLoading: addressNotAvailable,
		onClick: copyToClipboard,
		testID: 'ncw-receive-depositChilizChz-copyToClipboard-button',
		text: intlText.copyButtonText
	} satisfies ButtonProps;

	const navToNcwHome = () => push('/nc-wallet');

	const backButtonProps = {
		color: 'neutral',
		onClick: navToNcwHome,
		testID: 'ncw-receive-depositChilizChz-backToWallet-button',
		text: intlText.backButtonText,
		variant: 'text'
	} satisfies ButtonProps;

	return (
		<S.Container>
			<S.TitleRow>
				<S.ChainIcon height={16} style={{ display: 'inline' }} width={16} />
				<S.TitleText variant="text3">{intlText.cardTitle}</S.TitleText>
			</S.TitleRow>
			<S.StyledText variant="text8">
				<S.StyledTextOrSkeleton isLoading={isLoading}>{blockchainAddress}</S.StyledTextOrSkeleton>
				{askUserToAwaitAddress && <S.WaitForAddress>{intlText.waitForAddress}</S.WaitForAddress>}
			</S.StyledText>
			<S.CardRow>
				<Button {...copyButtonProps} />
			</S.CardRow>
			<S.LastRow>
				<Button {...backButtonProps} />
			</S.LastRow>
		</S.Container>
	);
};

namespace S {
	export const Container = styled.section`
		display: flex;
		flex-direction: column;
		align-items: center;
		background-color: ${({ theme }) => theme.colors.backgrounds.main.container};
		gap: ${({ theme }) => theme.spacings.xsmall};
		border-radius: ${({ theme }) => theme.spacings.xxsmall};
		padding: ${({ theme }) => `${theme.spacings.xlarge} ${theme.spacings.small}`};
		width: 100%;
	`;

	export const CardRow = styled.div`
		justify-content: center;
	`;

	export const TitleRow = styled.div``;

	export const TitleText = styled(Text)`
		display: inline;
		vertical-align: middle;
		margin-inline: ${({ theme }) => theme.spacings.xxxsmall};
	`;

	export const ChainIcon = styled(ChilizChainCircleIcon)`
		display: inline;
		vertical-align: middle;
	`;

	export const AddressRow = styled.div<{ $isLoading: boolean }>`
		width: 100%;
		display: flex;
		align-items: center;
		flex-direction: column;
		justify-content: center;
		margin-top: ${({ $isLoading, theme }) => ($isLoading ? `` : `${theme.spacings.medium}`)};
		margin-bottom: ${({ theme }) => theme.spacings.xlarge};
	`;

	export const Address = styled(Text)`
		text-align: center;
		color: ${({ theme }) => theme.colors.text.brand.enabled};
	`;

	export const LastRow = styled(CardRow)`
		margin-top: ${({ theme }) => theme.spacings.xxxsmall};
	`;

	export const StyledText = styled(Text)`
		display: inline-block;
		color: ${({ theme }) => theme.colors.text.brand.enabled};
		margin-bottom: ${({ theme }) => theme.spacings.large};
		min-height: 2.1lh;
		text-align: center;
		word-break: break-all;
	`;

	export const StyledTextOrSkeleton = styled(TextLineOrSkeleton)`
		height: 2lh;
		width: 23em;
	`;

	export const WaitForAddress = styled.div`
		width: 23em;
	`;
}
